import {CommonModule} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {Component, inject, input, model, OnChanges, SimpleChanges} from '@angular/core';
import {Router, RouterModule} from '@angular/router';
import {DialogComponent} from '@components/dialogs/dialog/dialog.component';
import {IconBoldCloseSquareComponent} from '@components/icons/icon-bold-close-square/icon-bold-close-square.component';
import {IconEyeComponent} from '@components/icons/icon-eye/icon-eye.component';
import {IconLocationAddComponent} from '@components/icons/icon-location-add/icon-location-add.component';
import {DemandaAcaoGuardService} from '@core/guards/demanda-acao.guard';
import {Demanda} from '@core/models/demanda';
import {DemandaService} from '@core/services/demanda.service';
import {
    VisualizarReforcarDemandaDialog
} from '@pages/demandas/visualizar-reforcar-demanda-dialog/visualizar-reforcar-demanda-dialog.component';
import {DatePtbrPipe} from '@shared/pipes/date-format.pipe';
import {MessageService, TreeNode} from 'primeng/api';
import {ButtonModule} from 'primeng/button';
import {Skeleton} from 'primeng/skeleton';
import {ToastModule} from 'primeng/toast';
import {TooltipModule} from 'primeng/tooltip';
import {TreeTableModule, TreeTableNodeExpandEvent} from 'primeng/treetable';

@Component({
    selector: 'app-detalhes-demanda-dialog',
    standalone: true,
    imports: [
        CommonModule,
        DialogComponent,
        ButtonModule,
        TooltipModule,
        ToastModule,
        RouterModule,
        DatePtbrPipe,
        Skeleton,
        TreeTableModule,
        VisualizarReforcarDemandaDialog,
        IconEyeComponent,
        IconLocationAddComponent,
        IconBoldCloseSquareComponent
    ],
    templateUrl: './detalhes-demanda-dialog.component.html',
    styleUrl: './detalhes-demanda-dialog.component.css',
    providers: [MessageService]
})
export class DetalhesDemandaDialogComponent implements OnChanges {
    visible = model(false);
    readonly isLoading = input(false);
    readonly demandasNaLocalizacao = input<Demanda[] | null>(null);

    treeDemands: TreeNode[] | undefined;
    loadingLeafTable = false;

    showVisualizarDemandaDialog = false;
    showReforcarDemandaDialog = false;
    demandaSelecionadaId: number | null = null;

    private readonly demandaAcaoGuardService = inject(DemandaAcaoGuardService);

    constructor(
        private readonly router: Router,
        private readonly DemandaService: DemandaService,
        private readonly messageService: MessageService
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.updateTableData();
    }

    visualizarDemanda(demanda: Demanda): void {
        this.demandaSelecionadaId = demanda.id ?? null;
        this.showVisualizarDemandaDialog = true;
    }

    abrirModalReforcarDemanda(demanda: Demanda): void {
        if (!this.demandaAcaoGuardService.canProceed('reforcar')) return;
        this.demandaSelecionadaId = demanda.id ?? null;
        this.showReforcarDemandaDialog = true;
    }

    onReforcarDemandaSuccess(): void {
        if (!this.demandaSelecionadaId) return;
        this.DemandaService.listarHistoricoReforcosPorDemandaId(this.demandaSelecionadaId).subscribe({
            next: (reforcos) => {
                const childrenNodes = reforcos.map(reforco => ({
                    key: reforco.id?.toString(),
                    data: {
                        id: reforco.id,
                        protocolo: reforco.protocolo,
                        tipoObra: reforco.tipoObra,
                        autor: reforco.autor,
                        representacao: reforco.representacao,
                        data: reforco.data,
                        situacao: reforco.situacao,
                    },
                    leaf: true,
                }));
                this.treeDemands = this.treeDemands?.map(node => {
                    if (node.key === this.demandaSelecionadaId?.toString()) {
                        return {...node, children: childrenNodes, expanded: true, leaf: false, disabled: false};
                    }
                    return node;
                });
            },
        });
    }

    fecharModal(): void {
        this.visible.set(false);
    }

    convertDataToTreeNode(data: Demanda[]) {
        this.treeDemands = data.map((demanda) => ({
            key: demanda.id?.toString(),
            data: {
                id: demanda.id,
                protocolo: demanda.protocolo,
                tipoObra: demanda.tipoObra,
                autor: demanda.autor,
                representacao: demanda.representacao,
                data: demanda.data,
                situacao: demanda.situacao,
            },
            leaf: !demanda.possuiReforco,
            disabled: !demanda.possuiReforco,
            children: demanda.reforcos
                ? demanda.reforcos.map((reforco) => ({
                    key: reforco.id?.toString(),
                    data: {
                        id: reforco.id,
                        protocolo: reforco.protocolo,
                        tipoObra: reforco.tipoObra,
                        autor: reforco.autor,
                        representacao: reforco.representacao,
                        data: reforco.data,
                        situacao: reforco.situacao,
                    },
                    leaf: true,
                }))
                : []
        }));
    }

    onNodeExpand(event: TreeTableNodeExpandEvent): void {
        const node = event.node;
        const demandaId = Number(node.key);


        if (node.children && node.children.length > 0) {
            node.expanded = true;
            return;
        }

        this.loadingLeafTable = true;

        this.DemandaService.listarHistoricoReforcosPorDemandaId(demandaId).subscribe({
            next: (reforcos) => {

                const childrenNodes = reforcos.map(reforco => ({
                    key: reforco.id?.toString(),
                    data: {
                        id: reforco.id,
                        protocolo: reforco.protocolo,
                        tipoObra: reforco.tipoObra,
                        autor: reforco.autor,
                        representacao: reforco.representacao,
                        data: reforco.data,
                        situacao: reforco.situacao,
                    },
                    leaf: true
                }));

                this.treeDemands = this.treeDemands?.map(rootNode => {
                    if (rootNode.key === node.key) {
                        return {
                            ...rootNode,
                            children: childrenNodes,
                            expanded: true
                        };
                    }
                    return rootNode;
                });

                this.loadingLeafTable = false;
            },
            error: (err) => {
                this.loadingLeafTable = false;
                console.error('Error loading children for node:', node.key, err);
                this.showReforcoLoadError(err);
            }
        });
    }

    private showReforcoLoadError(err: unknown): void {
        console.error('Erro ao carregar reforços:', err);

        let errorMessage = 'Falha ao carregar os reforços da demanda.';
        if (err instanceof HttpErrorResponse && err.error && typeof err.error === 'object' && 'message' in err.error) {
            errorMessage = err.error.message;
        }

        this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: errorMessage,
            life: 5000
        });
    }

    private updateTableData(): void {
        const demandas = this.demandasNaLocalizacao();
        if (demandas && demandas.length > 0) {
            this.convertDataToTreeNode(demandas);
        } else {
            this.treeDemands = [];
        }
    }

}
